import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { emailService } from '../lib/emailService';
import { getTicketAllocationSettings, calculateTicketAllocationLogic } from '../lib/ticketAllocationUtils';
import { Event, EventCategory, DriverProfile } from './useEvents';
import { isRegistrationOpen } from '../utils/dateUtils';

export const useEventRegistration = (
  userId: string | undefined,
  userEmail: string | undefined,
  userFullName: string | undefined,
  registrations: any[],
  driverProfiles: DriverProfile[],
  fetchUserRegistrations: () => Promise<void>
) => {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<EventCategory | null>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedVehicleFilter, setSelectedVehicleFilter] = useState<'car' | 'bike'>('car');
  const [isRegistering, setIsRegistering] = useState(false);
  const [includeToolCar, setIncludeToolCar] = useState(false);
  const [toolCarCount, setToolCarCount] = useState(0);

  const handleEventClick = (event: Event, userRole: string) => {
    // Only allow drivers to click on events
    if (userRole !== 'driver') return;
    setSelectedEvent(event);
    setSelectedVehicleFilter('car'); // Default to car when opening new event
    setIncludeToolCar(false); // Default to false until we check settings
    setToolCarCount(0); // Default to 0 until we check settings
    
    // Tool car count will be set when ticket settings are loaded in the modal
    fetchTicketSettings(event.id);
  };

  const fetchTicketSettings = async (eventId: string) => {
    try {
      const settings = await getTicketAllocationSettings(eventId);
      if (settings.tickets_enabled) {
        setToolCarCount(settings.tool_car_max_amount);
        setIncludeToolCar(settings.tool_car_max_amount > 0);
      } else {
        setToolCarCount(0);
        setIncludeToolCar(false);
      }
    } catch (error) {
      console.error('Error fetching ticket settings:', error);
      setToolCarCount(0);
      setIncludeToolCar(false);
    }
  };

  const handleCategorySelect = (category: EventCategory) => {
    if (selectedEvent && !isRegistrationOpen(selectedEvent.event_date)) {
      toast.error('Registration for this event has closed.');
      return;
    }
    setSelectedCategory(category);
    setShowRegistrationModal(true);
  };

  const handleRegistration = async () => {
    if (!selectedCategory || !selectedEvent || !userId) return;

    if (!isRegistrationOpen(selectedEvent.event_date)) {
      toast.error('Registration for this event has closed.');
      return;
    }

    setIsRegistering(true);
    try {
      // Find matching driver profile
      const vehicleTypeMap = { car: 'auto', bike: 'motorcycle' };
      const requiredVehicleType = vehicleTypeMap[selectedCategory.vehicle_type];
      
      const driverProfile = driverProfiles.find(
        profile => profile.vehicle_type === requiredVehicleType && profile.is_complete
      );

      if (!driverProfile) {
        toast.error(`You need a complete ${selectedCategory.vehicle_type} registration to participate in this category`);
        setIsRegistering(false);
        return;
      }

      // Get existing registrations for this event to calculate proper ticket allocation
      const existingRegistrations = registrations
        .filter(reg => reg.event_id === selectedEvent.id)
        .map(reg => {
          // Find the category details for this registration
          const categoryDetails = selectedEvent.event_categories.find(cat => cat.id === reg.category_id);
          return categoryDetails || null;
        })
        .filter(Boolean);

      // Get ticket allocation settings for this event
      const ticketSettings = await getTicketAllocationSettings(selectedEvent.id);
      console.log("Ticket settings for event:", ticketSettings);

      // Calculate ticket and wristband allocation
      const { categories, totalTickets, totalWristbands, toolCarCount: actualToolCarCount } = 
        calculateTicketAllocationLogic(
          [selectedCategory],
          existingRegistrations,
          includeToolCar,
          toolCarCount,
          ticketSettings
        );
      
      console.log("Tool car count from calculation:", actualToolCarCount);
      
      // Get the updated category with correct ticket/wristband counts
      const categoryWithAllocation = categories[0];

      // Calculate total registration fee
      const totalRegistrationFee = selectedCategory.registration_fee;

      const { data: registration, error: bookingError } = await supabase
        .from('event_registrations')
        .insert([
          {
            event_id: selectedEvent.id,
            category_id: selectedCategory.id,
            user_id: userId,
            driver_profile_id: driverProfile.id,
            amount_paid: totalRegistrationFee,
            has_tool_car: includeToolCar && ticketSettings.tickets_enabled,
            tool_car_count: (includeToolCar && ticketSettings.tickets_enabled) ? actualToolCarCount : 0
          }
        ])
        .select()
        .single();

      if (bookingError) throw bookingError;

      // Get all categories the user is registered for in this event
      const userEventRegistrations = registrations.filter(
        r => r.event_id === selectedEvent.id && r.user_id === registration.user_id
      );
      
      // Add the new registration to the list
      userEventRegistrations.push({
        id: registration.id,
        event_id: selectedEvent.id,
        category_id: selectedCategory.id,
        user_id: userId,
        amount_paid: totalRegistrationFee,
        status: 'pending',
        payment_status: 'pending',
        created_at: new Date().toISOString(),
        event: {
          name: selectedEvent.name,
          event_date: selectedEvent.event_date
        },
        category: {
          name: selectedCategory.name,
          vehicle_type: selectedCategory.vehicle_type,
          registration_fee: selectedCategory.registration_fee
        }
      });
      
      try {
        if (userEmail && userFullName) {
          // Calculate total registration fee
          const totalAmount = userEventRegistrations.reduce((sum, r) => sum + r.amount_paid, 0);
          
          // Prepare email data with all categories
          const emailData = {
            driver_name: userFullName,
            event_name: selectedEvent.name,
            event_date: formatDateForDisplay(selectedEvent.event_date),
            registration_deadline: formatDateForDisplay(selectedEvent.registration_deadline),
            categories: categories,
            total_amount: totalAmount,
            has_tool_car: includeToolCar && ticketSettings.tickets_enabled,
            tool_car_count: actualToolCarCount,
            tickets_enabled: ticketSettings.tickets_enabled,
            event_id: selectedEvent.id
          };

          console.log("Email data being sent:", JSON.stringify(emailData, null, 2));

          const emailResult = await emailService.sendEventRegistrationConfirmation(
            userId,
            userEmail,
            emailData
          );

          if (emailResult.success) {
            toast.success('Registration successful! Confirmation email sent.');
          } else {
            // Show warning but don't fail the registration
            if (emailResult.details && emailResult.details.includes('Email service is not configured')) {
              toast.success('Registration successful! (Email service temporarily unavailable)', {
                description: 'Your registration is confirmed. You will receive confirmation once email service is restored.',
                duration: 6000,
                icon: '⚠️'
              });
            } else {
              toast.success('Registration successful! (Email delivery failed)', {
                description: 'Your registration is confirmed, but we could not send the confirmation email.',
                duration: 5000,
                icon: '⚠️'
              });
            }
          }
        } else {
          toast.success('Registration successful!');
        }
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't fail the registration if email fails
        toast.success('Registration successful! (Email delivery failed)', {
          description: 'Your registration is confirmed, but we could not send the confirmation email.',
          duration: 5000,
          icon: '⚠️'
        });
      }

      setShowRegistrationModal(false);
      setSelectedCategory(null);
      setIncludeToolCar(false);
      setToolCarCount(0);
      fetchUserRegistrations(); // Refresh registrations
    } catch (error) {
      console.error('Error registering for event:', error);
      if (error instanceof Error) {
        // Check if it's an event buffer error
        if (error.message.includes('big event') && error.message.includes('buffer')) {
          toast.error(error.message);
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Failed to register for event');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleUnregister = async (registrationId: string, categoryName: string) => {
    // Find the registration to check its payment status
    const registration = registrations.find(reg => reg.id === registrationId);
    
    // If registration is paid, don't allow unregistering
    if (registration && registration.payment_status === 'paid') {
      toast.error('Cannot unregister from a paid category. Please contact support for assistance.');
      return;
    }
    
    if (!confirm(`Are you sure you want to unregister from ${categoryName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('id', registrationId)
        .eq('user_id', userId); // Extra security check

      if (error) throw error;

      toast.success('Successfully unregistered from the event');
      fetchUserRegistrations(); // Refresh registrations
    } catch (error) {
      console.error('Error unregistering from event:', error);
      toast.error('Failed to unregister from event');
    }
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return {
    selectedEvent,
    setSelectedEvent,
    selectedCategory,
    setSelectedCategory,
    showRegistrationModal,
    setShowRegistrationModal,
    selectedVehicleFilter,
    setSelectedVehicleFilter,
    isRegistering,
    includeToolCar,
    setIncludeToolCar,
    toolCarCount,
    setToolCarCount,
    handleEventClick,
    handleCategorySelect,
    handleRegistration,
    handleUnregister
  };
};